export function computeMissingLocationDefaults (nodes, currentLocations) {
  const assignedUserIds = new Set(
    (Array.isArray(currentLocations) ? currentLocations : [])
      .filter(loc => typeof loc === 'string' && loc.includes('-'))
      .map(loc => loc.split('-')[0])
  )

  const defaults = []
  ;(Array.isArray(nodes) ? nodes : []).forEach(group => {
    ;(group?.children || []).forEach(user => {
      if (!Array.isArray(user.children) || user.children.length === 0) return
      const userId = String(user.children[0].value).split('-')[0]
      if (assignedUserIds.has(userId)) return
      defaults.push(user.children[0].value)
      // Un usuario aparece una vez por grupo de ubicaciones: sin esto recibía la primera de
      // CADA grupo (TKT-2245, validación 29-sep).
      assignedUserIds.add(userId)
    })
  })

  return defaults
}

// TKT-2225 — Meta de ejecucion de las actividades de rutina.
//
// La meta que muestra el modal de agenda no es un campo: es el numero de filas de `events`
// que el calendario surtio para ese dia. Cuando la regeneracion de una rutina deja filas
// repetidas del mismo slot, la meta se infla sola ("Formularios completados 1/2" en una
// actividad configurada 1 vez al dia).
//
// Colapsar por slot solo es correcto en las actividades con HORARIO FIJO (programacion por
// dias y horas): en el backend unicamente `generateByGoals` multiplica por la meta
// configurada (ProcessRoutinesJob:990); las ramas por horario llaman `handleActivity` con el
// default `goal = 1`, asi que ahi toda segunda fila del mismo slot es un duplicado y nunca
// una meta legitima.
//
// Las metas de verdad (`programming_type = 'goals'`) se generan SIEMPRE arrancando el ciclo en
// `startOfDay()`, o sea con hora 00:00:00. Por eso esas filas no se colapsan por slot: son N
// ejecuciones legitimas e identicas entre si. De ellas solo se descarta la repeticion del MISMO
// id, que no es una ejecucion extra sino la replicacion por invitados de
// `CalendarController@index` (hace `$newEvent->id = $event->id` y puede emitir la misma fila
// dos veces).
//
// El criterio se equivoca, si acaso, hacia el lado seguro: una actividad por horario que
// empezara exactamente a las 00:00 no se deduplicaria (el bug seguiria a la vista), pero
// jamas se colapsa una meta configurada.
export function dedupeRoutineExecutions (events) {
  if (!Array.isArray(events) || events.length < 2) return events

  const indexByKey = new Map()
  const result = []

  events.forEach(event => {
    const key = hasFixedSchedule(event) ? slotKey(event) : `id|${event.id}`

    if (!indexByKey.has(key)) {
      indexByKey.set(key, result.length)
      result.push(event)
      return
    }

    // Entre dos filas de la misma ejecucion nos quedamos con la que trae avance, para no
    // borrar de la vista el formulario que el usuario ya contesto.
    const index = indexByKey.get(key)
    if (completionRank(event) > completionRank(result[index])) result[index] = event
  })

  return result
}

// Las filas de meta (`goals`) siempre nacen en startOfDay; cualquier otra hora de inicio viene
// de una programacion por dias y horas.
function hasFixedSchedule (event) {
  return Boolean(event.start_date) && event.start_date.slice(11, 19) !== '00:00:00'
}

// Identidad de una ejecucion con horario fijo: misma actividad, mismo usuario, misma ubicacion
// y mismo rango horario exacto.
function slotKey (event) {
  const activity = event.event_id != null ? event.event_id : event.id
  const location = event.location ? event.location.id : 'sin-ubicacion'
  return `slot|${activity}|${event.assigned_to}|${location}|${event.start_date}|${event.end_date}`
}

// "Con avance" = ya tiene ocurrencia/respuesta registrada. `status` es el de las ocurrencias y
// `value` el de las tareas de plan de accion.
function completionRank (event) {
  return (event.occurrence_id || event.status === 1 || event.value === 1) ? 1 : 0
}

// TKT-2270 — En una rutina por dias y horas un usuario con varias ubicaciones
// (ej. COACALCO y PASEO VENTURA) debe poder recibir un checklist en cada una.
// El selector "Usuarios con mas de 1 ubicacion" pasa a permitir marcar varias;
// esta funcion recalcula newEvent.locations aplicando la seleccion del usuario
// editado sin tocar las ubicaciones que otros usuarios ya tienen asignadas.
//
// - currentLocations: newEvent.locations completo (`${userId}-${locationId}`).
// - userOptionValues: todas las opciones del usuario editado (su universo).
// - selectedValues: las opciones que ese usuario tiene marcadas ahora.
export function mergeUserLocationSelection (currentLocations, userOptionValues, selectedValues) {
  const universe = new Set(Array.isArray(userOptionValues) ? userOptionValues : [])
  // Se conservan intactas las selecciones de los demas usuarios.
  const others = (Array.isArray(currentLocations) ? currentLocations : [])
    .filter(loc => !universe.has(loc))
  // Solo se aceptan valores que pertenezcan al universo del usuario editado.
  const selected = (Array.isArray(selectedValues) ? selectedValues : [])
    .filter(loc => universe.has(loc))

  return Array.from(new Set([...others, ...selected]))
}

/**
 * TKT-2245 Fase 1 — opciones de $q.notify para un error de guardado en Rutinas.
 *
 * El backend responde HTTP 409 con un mensaje accionable cuando el guard por
 * granularidad rechaza la operacion ("Hay un cambio de usuarios o fechas en proceso
 * desde hace N minutos, solicitado por X..."). Se muestra tal cual y con mas tiempo
 * en pantalla; para cualquier otro error se conserva el texto generico de siempre.
 *
 * `failed` es la llave que usa RoutinesController@finish; `message` la del resto.
 */
export function serverErrorNotify (error, fallback) {
  // El rechazo llega en DOS formas y hay que aceptar las dos:
  //
  //  - `src/api/responser.js` (el interceptor global) hace `Promise.reject(error.response)`,
  //    o sea propaga la RESPUESTA: `{ status, data }` a pelo, sin `.response`.
  //  - un rechazo que no pase por el interceptor conserva la forma de axios: `{ response }`.
  //
  // Leyendo solo `error.response`, el caso real —el del interceptor— caia siempre al texto
  // generico, y el mensaje accionable del guard de la Fase 1 no llegaba nunca al usuario.
  // Verificado en la app: con una solicitud global viva, el 409 se veia como "Error al
  // guardar." en vez de explicar que hay un cambio en proceso.
  const response = (error && error.response) || error || {}
  const data = response.data || {}
  const isGuardRejection = response.status === 409

  return {
    color: 'red',
    message: data.message || data.failed || fallback,
    timeout: isGuardRejection ? 6000 : 1500
  }
}

// TKT-2245 Fase 5 — forma canonica de `days` en una actividad por dias y horas.
//
// Espeja App\Helpers\RoutineWeekDays del backend. El generador semanal recorre `days` y
// por cada entrada camina la semana COMPLETA desde la misma fecha de referencia, asi que
// un dia repetido —o el mismo dia con otra capitalizacion, porque el matcher no
// distingue— generaba el set entero dos veces.
//
// La UI por si sola no puede producir eso: los chips emiten valores fijos del catalogo.
// El dato sucio llega al HIDRATAR una actividad existente que ya lo tiene guardado asi
// (cliente viejo, o dato previo a la normalizacion). Sin limpiarlo ahi, el chip no
// coincide con ninguna opcion, el usuario no lo ve, y al guardar se reenvia verbatim y se
// vuelve a duplicar. De ahi que se normalice al entrar, no al salir.
const WEEK_DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']

export function normalizeWeekDays (days) {
  if (!Array.isArray(days)) return []

  const seen = new Set(
    days
      .filter(day => typeof day === 'string')
      .map(day => day.trim().toLowerCase())
      .filter(day => WEEK_DAYS.includes(day))
  )

  return WEEK_DAYS.filter(day => seen.has(day))
}

// TKT-2245 — normaliza salvo cuando `days` NO son nombres de día.
//
// El único caso es la recurrencia mensual "En los días..." (`monthlyType === 'atDays'`),
// donde `days` guarda NÚMEROS de día del mes (29, 30, 31). Aplicar la normalización ahí
// los borraba al hidratar: el usuario abría una actividad mensual configurada y se
// encontraba el calendario vacío y la leyenda "se repetirá los días mensualmente" sin
// ningún día, y al guardar se llevaba la pérdida a la base. Espeja
// App\Helpers\RoutineWeekDays::normalizeFor() del backend.
export function holdsMonthDays (frequency, monthlyType) {
  return frequency === 'monthly' && monthlyType === 'atDays'
}

export function normalizeDaysFor (days, frequency, monthlyType) {
  if (holdsMonthDays(frequency, monthlyType)) return days

  return normalizeWeekDays(days)
}

// TKT-2245 Fases 6 y 7 — qué pares usuario-ubicación publica el configurador.
//
// `newEvent.locations` es lo que el backend usa para decidir qué relaciones de la
// actividad siguen vivas: lo que no aparece aquí, se borra. De ahí que esto importe más
// de lo que parece.
//
// Con visitas múltiples encendidas hay DOS fuentes: las franjas horarias y las
// asignaciones por día. Publicar sólo las franjas dejaba fuera a los días configurados
// por la Fase 6 sin franja propia; el backend borraba esa relación y ese día se quedaba
// sin agenda, en silencio. La generación ya prevé el caso —un día sin franjas cae a la
// asignación por día— pero sin la relación no tiene a qué caer.
const claveUsuarioUbicacion = (userId, locationId) => `${userId}-${locationId}`

export function dayAssignmentLocationKeys (userDayAssignments, perteneceALaRutina = () => true) {
  const claves = new Set()

  Object.keys(userDayAssignments || {}).forEach(userId => {
    if (!perteneceALaRutina(userId)) return

    const asignaciones = userDayAssignments[userId] || {}
    Object.keys(asignaciones).forEach(day => {
      const locationId = asignaciones[day]
      if (locationId) claves.add(claveUsuarioUbicacion(userId, locationId))
    })
  })

  return claves
}

export function timeSlotLocationKeys (userDayTimeSlots, perteneceALaRutina = () => true) {
  const claves = new Set()

  Object.keys(userDayTimeSlots || {}).forEach(userId => {
    if (!perteneceALaRutina(userId)) return

    const porDia = userDayTimeSlots[userId] || {}
    Object.keys(porDia).forEach(day => {
      ;(porDia[day] || []).forEach(slot => {
        if (slot && slot.locationId) claves.add(claveUsuarioUbicacion(userId, slot.locationId))
      })
    })
  })

  return claves
}

/**
 * Las ubicaciones que el configurador debe publicar, según qué opciones estén encendidas.
 * Sin configuración por día no publica ninguna: manda el selector de ubicaciones normal.
 */
export function routineLocationKeys (
  { showDayConfig, showMultipleVisits, userDayAssignments, userDayTimeSlots, currentLocations = [] },
  perteneceALaRutina = () => true
) {
  if (!showDayConfig) return []

  const porDia = dayAssignmentLocationKeys(userDayAssignments, perteneceALaRutina)
  const deFranjas = showMultipleVisits ? timeSlotLocationKeys(userDayTimeSlots, perteneceALaRutina) : new Set()

  // TKT-2245 H6 (QA de Víctor) — quien no aparece en la configuración conserva sus relaciones.
  // Para la generación ese usuario no está restringido por día; si aquí no aportaba ninguna,
  // el backend veía sus relaciones "ausentes" y las marcaba 'deleted', y se quedaba sin agenda.
  // Pasa con cualquier usuario sin configurar, y siempre con los que el selector muestra sin
  // ubicaciones porque su distrito no está en el árbol de grupos.
  const configurados = new Set([
    ...Object.keys(userDayAssignments || {}),
    ...(showMultipleVisits ? Object.keys(userDayTimeSlots || {}) : [])
  ].map(String))

  const intactos = (currentLocations || []).filter(clave => {
    const userId = String(clave).split('-')[0]
    return !configurados.has(userId) && perteneceALaRutina(userId)
  })

  return Array.from(new Set([...deFranjas, ...porDia, ...intactos]))
}

/**
 * TKT-2245 Fases 6 y 7 — estado de los toggles avanzados al ABRIR una actividad.
 *
 * `showDayConfig` y `showMultipleVisits` son estado de la vista y nunca se persistieron;
 * lo que sí queda guardado es el resultado (`userDayAssignments`, `userDayTimeSlots`, y
 * el interruptor `hasMultipleVisits` que lee la generación). Sin derivarlos, reabrir una
 * actividad ya configurada mostraba los dos toggles apagados: el usuario no veía su
 * propia configuración por día y tenía que encender el toggle para que reapareciera.
 *
 * Se derivan sólo hacia ENCENDIDO: si el payload ya trae el valor explícito, manda él.
 */
const tieneAlgo = (valor) => Boolean(valor) && typeof valor === 'object' && Object.keys(valor).length > 0

export function resolveAdvancedToggles (payload) {
  if (!payload || payload.programmingType === 'goals') {
    return { showDayConfig: Boolean(payload && payload.showDayConfig), showMultipleVisits: false }
  }

  return {
    showDayConfig: Boolean(payload.showDayConfig) ||
      tieneAlgo(payload.userDayAssignments) ||
      tieneAlgo(payload.userDayTimeSlots),
    showMultipleVisits: Boolean(payload.showMultipleVisits) ||
      Boolean(payload.hasMultipleVisits) ||
      tieneAlgo(payload.userDayTimeSlots)
  }
}

// TKT-2245 H5 — estados de rutina en los que una actividad YA generada se edita "en vivo":
// los campos que ninguna fase reactiva (tipo de programación, frecuencia, "todo el día"…)
// quedan bloqueados. Antes sólo contaban 'active' y 'rescheduled', así que con la rutina
// 'extended' (una extensión de vigencia en curso) o 'scheduled' (programación pendiente) el
// diálogo dejaba cambiar el tipo de programación de una actividad generada: el guardado
// rebotaba, o colaba campos del otro plano. Lo decide el backend; aquí sólo se dibuja.
export const ROUTINE_LIVE_STATUSES = ['active', 'rescheduled', 'extended', 'scheduled']

export function isRoutineLive (status) {
  return ROUTINE_LIVE_STATUSES.includes(status)
}

// TKT-2245 H3 (QA de Víctor) — una rutina cuya fecha final ya pasó. Terminar hoy no es
// "vencida". Se compara por día, no por instante: end_date es una fecha sin hora.
export function isRoutineExpired (routine, today = new Date()) {
  if (!routine || !routine.end_date || routine.status === 'finished') return false
  const end = String(routine.end_date).slice(0, 10)
  const hoy = today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0')
  return end < hoy
}

// TKT-2245 (validación 29-sep, H8 con config por día): días del calendario VIGENTE que el
// usuario tiene asignados. Al cambiar la recurrencia, el mapa guardado conserva llaves del
// modo anterior (p. ej. `monthly_15` tras pasar a "El tercer miércoles"); contarlas hacía
// que el resumen dijera "1/1 días asignados - Completo" de un día que ya no existe.
export function assignedDayValues (assignments, dayValues) {
  if (!assignments || typeof assignments !== 'object') return []
  return dayValues.filter(day => assignments[day] !== undefined && assignments[day] !== null)
}

// TKT-2245 G1 (QA de Víctor): el recuadro del preview de la edición global sólo se muestra
// si trae algo que decir. Extender la vigencia sin tocar usuarios responde todo en cero, y
// quedaba un recuadro vacío en lugar del spinner.
export function routinePreviewHasInfo (preview) {
  if (!preview) return false
  return ['usersAdded', 'usersRemoved', 'activitiesToGenerate', 'activitiesToDelete', 'usersExcluded']
    .some(key => Number(preview[key]) > 0)
}

// TKT-2245 (ronda 12-oct, sgarcia): la vista semanal de la previsualización quedaba en blanco.
// Una actividad con horario múltiple (F7) llega con `allDay:false` y SIN startHour/endHour
// (las horas viven en cada visita). El calendario daba por hecho que "no es todo el día" =
// "tiene hora", calculaba `time = 'Invalid date'` y `timeStartPos()` tronaba al pintar el
// cuerpo del día: se caía el render completo, sin encabezados. Sólo se pinta por hora lo que
// de verdad tiene un rango; el resto va en la franja de todo el día.
export function activityHasHours (data) {
  return !!data && !data.allDay && !!data.startHour && !!data.endHour
}
